import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { IconUnion as Union } from '../../core';

type CardProps = {
  className?: string;
  children?: ReactNode;
  title?: string;
  icon?: ReactNode;
  image?: string;
  category?: boolean;
  path?: string;
  titleTag?: 'h2' | 'h3';
};

const Card = ({
  className = '',
  children = null,
  title = '',
  icon = null,
  image = '',
  category = false,
  path = '',
  titleTag: TitleTag = 'h2',
}: CardProps) => {
  const navigate = useNavigate();

  return (
    <button
      onClick={() => navigate(path)}
      type="button"
      className={`${className} ${category ? 'card-category' : ''}`}
    >
      {!category ? (
        <>
          <div className="icons-wrapper">
            <div className="icon">{icon}</div>
            <Union className="icon-link" />
          </div>
          <TitleTag>{title}</TitleTag>
          <p>{children}</p>
        </>
      ) : (
        <>
          <div className="card-category-img">
            {image && <img className="img-responsive" src={image} alt={title} />}
            <Union className="icon-link" />
          </div>
          <div className="card-category-desc">
            <TitleTag>{title}</TitleTag>
            <p>{children}</p>
          </div>
        </>
      )}
    </button>
  );
};

const CardStyled = styled(Card)`
  &:not(.card-category) {
    border: 0.0625rem solid ${({ theme }) => theme.neutralBorder};
    background: ${({ theme }) => theme.neutralBackground};
    border-top-left-radius: 0.5rem;
    border-top-right-radius: 0.5rem;
    padding: 1rem;
    position: relative;
    text-align: left;
    min-height: 12.5rem;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    cursor: pointer;
    transition:
      background 0.3s ease-in-out,
      border-color 0.3s ease-in-out;

    &::before {
      content: '';
      position: absolute;
      bottom: -0.0625rem;
      left: -0.0625rem;
      height: 0.125rem;
      width: 0%;
      background: ${({ theme }) => theme.primaryBackgroundStrong};
      transition: width 0.3s ease-in-out;
    }

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 0.0625rem;
      background: ${({ theme }) => theme.primaryBackgroundMedium};
    }

    &:hover {
      background: ${({ theme }) => theme.primaryBackground};
      border-color: transparent;

      &::before {
        width: calc(100% + 0.125rem);
      }

      &::after {
        background: ${({ theme }) => theme.primaryBackgroundMedium};
      }

      .icon-link {
        color: ${({ theme }) => theme.neutralTextWeak};
      }
    }

    &:active {
      background: ${({ theme }) => theme.primaryBackgroundActive};
      border: 0.0625rem solid #2685a13d; /* tokens-allow: pending design decision */

      &::before {
        display: none;
      }

      &::after {
        background: ${({ theme }) => theme.primaryBackgroundMedium};
      }

      .icon-link {
        color: ${({ theme }) => theme.primaryText};
      }
    }

    .icons-wrapper {
      display: flex;
      align-items: start;
      justify-content: space-between;

      & + :is(h2, h3) {
        margin-top: 0.5rem;
      }
    }

    .icon {
      padding: 0.25rem;

      &-link {
        color: ${({ theme }) => theme.neutralTextWeak};
        transition: color 0.3s ease-in-out;
      }
    }

    & :is(h2, h3) {
      font-weight: 600;
      font-size: 1rem;
      line-height: 1.25;
      color: ${({ theme }) => theme.neutralText};
      margin-top: 0;
      margin-bottom: 0;
      letter-spacing: -0.01em;

      & + p {
        margin-top: 0.5rem;
        margin-bottom: 0;
      }
    }

    p {
      color: ${({ theme }) => theme.neutralTextWeak};
    }
  }

  &.card-category {
    display: flex;
    flex-direction: column;
    border: 0.0625rem solid ${({ theme }) => theme.neutralBorder};
    border-top-left-radius: 0.5rem;
    border-top-right-radius: 0.5rem;
    background: transparent;
    padding: 0;
    position: relative;
    overflow: hidden;
    text-align: left;
    cursor: pointer;

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 0.25rem;
      transition: background-color 0.3s ease-in-out;
    }

    &:hover {
      &::after {
        background-color: ${({ theme }) => theme.neutralBackgroundMedium};
      }
    }

    .icon-link {
      position: absolute;
      top: 1rem;
      right: 1rem;
      color: ${({ theme }) => theme.neutralTextWeak};
      transition: color 0.3s ease-in-out;
    }

    .card-category {
      &-img {
        display: flex;
      }

      &-desc {
        padding: 1.5rem 1.25rem 2rem;

        & :is(h2, h3) {
          color: ${({ theme }) => theme.neutralText};
          font-size: ${({ theme }) => theme.typography.h3Sb.fontSize};
          font-weight: ${({ theme }) => theme.typography.h3Sb.fontWeight};
          line-height: ${({ theme }) => theme.typography.h3Sb.lineHeight};
          letter-spacing: ${({ theme }) => theme.typography.h3Sb.letterSpacing};
          margin: 0 0 1rem 0;
          transition: color 0.3s ease-in-out;
        }

        p {
          color: ${({ theme }) => theme.neutralTextWeak};
          font-size: 0.875rem;
          font-weight: 400;
          line-height: 1.428571428571429;
          margin: 0;
        }

        &:hover {
          & :is(h2, h3) {
            color: ${({ theme }) => theme.primaryText};
          }
        }
      }
    }
  }
`;

export default CardStyled;
